import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Lock, Download, Smartphone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { API, setAuthToken, setStoredUser, getErrorMessage } from '../utils/api';

export const AuthPage = ({ onAuthSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [joinButton, setJoinButton] = useState(null);

  useEffect(() => {
    // Fetch join button config
    axios.get(`${API}/settings/join-button`)
      .then(res => {
        if (res.data?.enabled && res.data?.url) {
          setJoinButton(res.data);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    
    if (isStandalone) {
      return; // Already installed
    }

    // Always show install button (works on iOS and shows instructions, Android/Desktop will get prompt)
    setShowInstallButton(true);

    // For Android/Desktop, listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSModal(true);
      return;
    }

    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setShowInstallButton(false);
      }
      setDeferredPrompt(null);
    } else {
      // For browsers that don't support beforeinstallprompt, show generic instructions
      setShowIOSModal(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user } = response.data;

      setAuthToken(access_token);
      setStoredUser(user);
      onAuthSuccess(user);
    } catch (err) {
      setError(getErrorMessage(err, 'Email ou senha incorretos'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex justify-center mb-6">
            <img 
              src={process.env.REACT_APP_LOGO_URL || "/ferraz-logo.png"} 
              alt="Ferraz Team"
              className="h-24 w-auto"
              style={{
                filter: 'invert(1) brightness(1)'
              }}
            />
          </div>
          <h1 className="font-primary font-black text-4xl text-white uppercase tracking-tighter mb-2">
            Ferraz Team
          </h1>
          <p className="text-zinc-400 font-secondary">
            Sua jornada fitness gamificada
          </p>
        </motion.div>

        <motion.div
          className="glass-surface rounded-2xl p-8"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="font-primary font-bold text-2xl text-white uppercase text-center mb-6">
            Acesso Restrito
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  className="pl-10 bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white h-12"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Senha
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 bg-zinc-950/50 border-zinc-800 focus:border-neon-lime text-white h-12"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-neon-lime text-black font-black uppercase tracking-wider hover:bg-neon-lime/80 h-12 text-lg"
              data-testid="submit-button"
            >
              {loading ? 'Carregando...' : 'Entrar'}
            </Button>
          </form>
          
          <p className="text-center text-zinc-500 text-xs mt-6">
            Acesso exclusivo para participantes cadastrados
          </p>
        </motion.div>

        {/* Botão "Quero participar" - configurável pelo admin */}
        {joinButton && (
          <motion.div
            className="mt-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <a
              href={joinButton.url}
              target={joinButton.open_new_tab ? '_blank' : '_self'}
              rel={joinButton.open_new_tab ? 'noopener noreferrer' : undefined}
              data-testid="join-button"
            >
              <Button
                type="button"
                className="w-full bg-transparent border-2 border-neon-lime text-neon-lime font-black uppercase tracking-wider hover:bg-neon-lime hover:text-black transition-all duration-300 h-12 text-base"
              >
                {joinButton.text || 'Quero participar'}
              </Button>
            </a>
          </motion.div>
        )}

        {/* Botão Instalar App */}
        {showInstallButton && (
          <motion.div
            className="mt-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Button
              onClick={handleInstall}
              className="w-full bg-zinc-900 border border-neon-lime/30 text-white hover:bg-zinc-800 h-12"
              data-testid="install-app-button"
            >
              <Download className="w-5 h-5 mr-2 text-neon-lime" />
              Instalar Aplicativo
            </Button>
            <p className="text-center text-zinc-600 text-xs mt-2">
              Adicione à tela inicial para acesso rápido
            </p>
          </motion.div>
        )}
      </div>

      {/* Modal iOS Instructions */}
      {showIOSModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50 p-4"
          onClick={() => setShowIOSModal(false)}
        >
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="bg-zinc-900 border border-neon-lime/30 rounded-2xl p-6 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-neon-lime/20 rounded-xl flex items-center justify-center">
                <Smartphone className="w-6 h-6 text-neon-lime" />
              </div>
              <div>
                <h3 className="font-bold text-white text-lg">Instalar no iPhone</h3>
                <p className="text-zinc-500 text-sm">Siga os passos abaixo</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-zinc-800 rounded-full flex items-center justify-center text-neon-lime font-bold text-sm flex-shrink-0">
                  1
                </div>
                <p className="text-zinc-300 text-sm pt-1">
                  Toque no ícone <span className="text-neon-lime font-bold">Compartilhar</span> (quadrado com seta para cima) na barra do Safari
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-zinc-800 rounded-full flex items-center justify-center text-neon-lime font-bold text-sm flex-shrink-0">
                  2
                </div>
                <p className="text-zinc-300 text-sm pt-1">
                  Role para baixo e toque em <span className="text-neon-lime font-bold">"Adicionar à Tela de Início"</span>
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-zinc-800 rounded-full flex items-center justify-center text-neon-lime font-bold text-sm flex-shrink-0">
                  3
                </div>
                <p className="text-zinc-300 text-sm pt-1">
                  Toque em <span className="text-neon-lime font-bold">"Adicionar"</span> no canto superior direito
                </p>
              </div>
            </div>

            <Button
              onClick={() => setShowIOSModal(false)}
              className="w-full mt-6 bg-neon-lime text-black font-bold"
            >
              Entendi!
            </Button>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};
